import { DefaultService, RatingHistory } from '../generated-api';

/** Rating histories by player ID. */
export type RatingHistories = Record<string, RatingHistory>;

export const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Ratings are recalculated asynchronously after a write, so a response right after one may
 * predate it. Nothing is cached for this long, or the old data would stick for a full TTL.
 */
export const SETTLE_MS = 60 * 1000;

// Non-GET API routes that can change someone's rating history (any play can affect everyone
// who played after it, so these clear the whole cache rather than one league or player)
const RATING_WRITE_PATH =
  /\/(plays(\/[^/]+)?|live-plays\/[^/]+\/end|ratings\/recalculate-all|leagues\/[^/]+\/players(\/[^/]+)?)$/;

const cache = new Map<string, { expires: number; promise: Promise<RatingHistories> }>();
let settlesAt = 0;

/**
 * Rating histories in a league from one batched request, cached in memory.
 *
 * @param playerIds Players to fetch (default: all league members)
 * @param fresh Skip the cache, for callers that need a play that was only just logged
 */
export const getRatingHistories = (
  leagueId: string,
  { playerIds, fresh = false }: { playerIds?: string[]; fresh?: boolean } = {},
): Promise<RatingHistories> => {
  const key = `${leagueId}|${playerIds ? [...playerIds].sort().join(',') : '*'}`;
  const now = Date.now();

  const cached = cache.get(key);
  if (!fresh && cached && cached.expires > now) return cached.promise;

  const promise = Promise.resolve(
    DefaultService.getLeagueRatingHistoriesLeaguesLeagueIdRatingHistoriesGet({
      leagueId,
      playerIds: playerIds?.join(','),
    }),
  ).then(histories => Object.fromEntries(histories.map(history => [history.playerId, history])));

  if (now >= settlesAt) {
    const entry = { expires: now + CACHE_TTL_MS, promise };
    cache.set(key, entry);
    promise.catch(() => {
      if (cache.get(key) === entry) cache.delete(key);
    });
  }
  return promise;
};

export const invalidateRatingHistories = (): void => {
  cache.clear();
  settlesAt = Date.now() + SETTLE_MS;
};

/** Drops cached histories whenever a generated-API call changes plays, ratings or league members. */
export const installRatingHistoryInvalidation = (client: {
  interceptors: { response: { use: (ok: (r: any) => any) => unknown } };
}): void => {
  client.interceptors.response.use(response => {
    const method = String(response?.config?.method || 'get').toLowerCase();
    const path = String(response?.config?.url || '').split('?')[0];
    if (method !== 'get' && RATING_WRITE_PATH.test(path)) invalidateRatingHistories();
    return response;
  });
};

export const __resetForTests = (): void => {
  cache.clear();
  settlesAt = 0;
};
